<template>
  <section>
    <nav-component :titulo="'Abonos'" texto="Realizar abono" :link="link" />
    <b-breadcrumb align="is-left">
      <b-breadcrumb-item tag='router-link' to="/">Inicio</b-breadcrumb-item>
      <b-breadcrumb-item active>Abonos</b-breadcrumb-item>
    </b-breadcrumb>
    <h3 class="has-text-centered is-size-4 mb-3 has-text-weight-bold">
      Datos de {{ this.cuentaApartado && this.cuentaApartado.tipo === 'cuenta' ? 'cuenta' : 'apartado' }}
    </h3>
    <cartas-totales-filtradas :registros="datosCuentaApartado" />
    <div class="has-text-right mb-2">
      <b-button type="is-info is-light" size="is-small" icon-left="account-search"
        @click="abrirAuditoria">
        Auditar abonos duplicados
      </b-button>
    </div>
    <hr>
    <mensaje-inicial :titulo="'No se han registrado abonos'"
      :subtitulo="'Haz click el botón de la esquina para registrar un nuevo abono'" v-if="abonos.length < 1" />
    <template v-else>
      <h3 class="has-text-centered is-size-4 mb-3 has-text-weight-bold">
        Lista de abonos
      </h3>
      <div class="columns">
        <div class="column">
          <b-select v-model="perPage" @input="onPerPageChange">
            <option :value="5">5 por página</option>
            <option :value="10">10 por página</option>
            <option :value="15">15 por página</option>
            <option :value="20">20 por página</option>
            <option :value="null">Todos</option>
          </b-select>
        </div>
        <div class="column is-flex is-justify-content-end">
          <b-button type="is-primary" tag="a" :href="`#/pdf/abonos/${cuentaApartado && cuentaApartado.id}`"
            target="__blank" rel="noopener noreferrer">
            Imprimir
          </b-button>
        </div>
      </div>
      <b-table class="box" :data="abonos" :per-page="perPage || total || 1" :paginated="true" :pagination-simple="false"
        :current-page="currentPage" :backend-pagination="true" :total="total" @page-change="onPageChange"
        :pagination-position="'bottom'" :default-sort-direction="'asc'" :pagination-rounded="true">


        <b-table-column field="fecha" label="Fecha" v-slot="props">
          {{ formatoFechaCaracas(props.row.fecha) }}
        </b-table-column>

        <b-table-column field="monto" label="Monto Dólares" sortable searchable v-slot="props">
          ${{ formatoMonto(props.row.monto) }}
        </b-table-column>

        <b-table-column field="monto" label="Monto Bolívares" sortable searchable v-slot="props">
          {{ props.row.idTasa ? `Bs. ${formatoMonto(props.row.montoBs)}` : '-' }}
        </b-table-column>

        <b-table-column field="monto" label="Factura generada en" sortable searchable v-slot="props">
          {{ props.row.idTasa ? 'Bolívares' : 'Dólares' }}
        </b-table-column>

        <b-table-column field="metodo" label="Método de pago" sortable searchable v-slot="props">
          {{ props.row.metodo || props.row.simple }}
        </b-table-column>

        <b-table-column field="origen" label="Origen" sortable searchable v-slot="props">
          {{ props.row.origen || 'N/A' }}
        </b-table-column>

        <b-table-column field="ticket" label="Comprobante" v-slot="props">
          <b-button type="is-warning" icon-left="pencil-outline" tag="router-link"
            :to="{ name: 'EditarAbono', params: { id: props.row.id } }" v-if="can('ventas.actualizar_abono')">Editar</b-button>
          <b-button type="is-info" @click="generarComprobante(props)">
            <b-icon icon="ticket-outline">
            </b-icon>
          </b-button>
          <b-button type="is-danger" icon-left="trash-can-outline" @click="eliminarAbono(props.row)">Eliminar</b-button>
        </b-table-column>
      </b-table>
    </template>

    <!-- Auditoría de abonos duplicados: solo lectura. Muestra si esta cuenta quedó con
         sobrepago y qué abonos se repiten (misma cuenta, monto, método y origen). -->
    <b-modal v-model="mostrarAuditoria" has-modal-card trap-focus aria-role="dialog" aria-modal>
      <div class="modal-card" style="width: 92vw; max-width: 1000px">
        <header class="modal-card-head has-background-info">
          <p class="modal-card-title has-text-white">Auditoría de abonos duplicados</p>
          <button type="button" class="delete" @click="mostrarAuditoria = false" />
        </header>
        <section class="modal-card-body">
          <p v-if="cargandoAuditoria" class="has-text-centered py-4">Cargando…</p>
          <template v-else>
            <h4 class="title is-6">Cuentas con sobrepago</h4>
            <p v-if="!auditoria || auditoria.cuentas_con_sobrepago.length === 0"
              class="has-text-grey is-size-7 mb-4">
              Ninguna cuenta tiene abonos por encima de su total.
            </p>
            <b-table v-else :data="auditoria.cuentas_con_sobrepago" hoverable class="mb-4">
              <b-table-column field="id" label="Cuenta" v-slot="props">{{ props.row.id }}</b-table-column>
              <b-table-column field="nombreCliente" label="Cliente" v-slot="props">{{ props.row.nombreCliente }}</b-table-column>
              <b-table-column field="total" label="Total" v-slot="props">${{ formatoMonto(props.row.total) }}</b-table-column>
              <b-table-column field="pagado" label="Pagado" v-slot="props">${{ formatoMonto(props.row.pagado) }}</b-table-column>
              <b-table-column field="cantidad_abonos" label="Abonos" v-slot="props">{{ props.row.cantidad_abonos }}</b-table-column>
              <b-table-column field="exceso" label="Exceso" v-slot="props">
                <span class="has-text-danger has-text-weight-bold">${{ formatoMonto(props.row.exceso) }}</span>
              </b-table-column>
            </b-table>

            <h4 class="title is-6">Abonos repetidos</h4>
            <p v-if="!auditoria || auditoria.grupos_repetidos.length === 0"
              class="has-text-grey is-size-7">
              No hay abonos con el mismo monto, método y origen repetidos.
            </p>
            <b-table v-else :data="auditoria.grupos_repetidos" hoverable>
              <b-table-column field="idCuenta" label="Cuenta" v-slot="props">{{ props.row.idCuenta }}</b-table-column>
              <b-table-column field="monto" label="Monto" v-slot="props">${{ formatoMonto(props.row.monto) }}</b-table-column>
              <b-table-column field="veces" label="Veces" v-slot="props">
                <b-tag :type="props.row.veces > 1 ? 'is-danger' : 'is-light'">{{ props.row.veces }}</b-tag>
              </b-table-column>
              <b-table-column field="ids" label="IDs de abonos" v-slot="props">
                <span class="is-size-7">{{ props.row.ids }}</span>
              </b-table-column>
              <b-table-column field="primera" label="Del / Al" v-slot="props">
                <span class="is-size-7">{{ props.row.primera }} → {{ props.row.ultima }}</span>
              </b-table-column>
              <b-table-column field="origen" label="Origen" v-slot="props">{{ props.row.origen || 'N/A' }}</b-table-column>
            </b-table>

            <p class="is-size-7 has-text-grey mt-4">
              Esta auditoría no modifica ni borra nada: revisa cada caso y decide.
              Ojo: dos abonos legítimos con el mismo monto y método el mismo día también
              aparecen aquí.
            </p>
          </template>
        </section>
        <footer class="modal-card-foot">
          <b-button label="Cerrar" @click="mostrarAuditoria = false" />
        </footer>
      </div>
    </b-modal>

    <b-loading :is-full-page="true" v-model="cargando" :can-cancel="false"></b-loading>
  </section>
</template>
<script>
import MensajeInicial from '../Extras/MensajeInicial'
import NavComponent from '../Extras/NavComponent'
import HttpService from '../../Servicios/HttpService'
import Utiles from '../../Servicios/Utiles'
import CartasTotalesFiltradas from '../Extras/CartasTotalesFiltradas.vue'

export default {
  name: "AbonosComponent",
  components: { MensajeInicial, NavComponent, CartasTotalesFiltradas },

  data: () => ({
    tasa: null,
    cargando: false,
    abonos: [],
    perPage: 5,
    currentPage: 1,
    total: 0,
    cuentaApartado: null,
    datosCuentaApartado: [],
    link: null,
    auditoria: null,
    mostrarAuditoria: false,
    cargandoAuditoria: false,
  }),

  async mounted ()
  {
    const resultado = await HttpService.registrar('ventas.php', {
      accion: 'obtener_tasa'
    })

    this.tasa = resultado.valor
    await this.obtenerAbonos()
    // Sin deuda no hay nada que abonar, y sin el permiso el API rechazaria el registro.
    // Antes se consultaba `abonos.registrar`, una clave que NO existe en el catalogo de
    // permisos: nunca era true, la condicion nunca cortaba y el boton aparecia incluso
    // con la deuda en cero. La clave real es `ventas.realizar_abono`.
    if (Number(this.cuentaApartado.porPagar) === 0) return
    if (!this.can('ventas.realizar_abono')) return

    this.link = { name: 'RealizarAbono', params: { id: this.$route.params.id } }
  },

  methods: {
    /**
     * Auditoría (solo lectura) de esta cuenta: sobrepago y abonos repetidos.
     * Sirve para confirmar si el reporte de "abonos duplicados" dejó rastro en los datos.
     */
    async abrirAuditoria ()
    {
      this.mostrarAuditoria = true
      this.cargandoAuditoria = true

      try {
        this.auditoria = await HttpService.obtenerConConsultas('ventas.php', {
          accion: 'auditoria_abonos',
          idCuenta: this.$route.params.id,
        })
      } catch (error) {
        this.auditoria = null
        this.$buefy.toast.open({
          type: 'is-danger',
          message: (error && error.message) || 'No se pudo cargar la auditoría',
        })
      } finally {
        this.cargandoAuditoria = false
      }
    },

    formatoMonto (valor)
    {
      return Utiles.formatoMonto(valor)
    },

     generarComprobante ({ row })
     {
       // Get the local for this abono from localStorage
       const local = localStorage.getItem('abono-local-' + row.id) || null;
       const datos = { abono: row, cuenta: this.cuentaApartado, local };
       localStorage.setItem('comprobante-abono', JSON.stringify(datos))
       const ruta = this.$router.resolve({ name: 'PDFAbono' })
       window.open(ruta.href, '_blank')
     },

    async eliminarAbono (abono)
    {
      this.$buefy.dialog.confirm({
        message: '¿Estás seguro de que deseas eliminar este abono?',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        type: 'is-danger',
        onConfirm: async () =>
        {
          this.cargando = true
          try {
            const respuesta = await HttpService.obtenerConConsultas('ventas.php', {
              accion: 'eliminar_abono',
              id: abono.id
            })
            if (respuesta || respuesta.success) {
              this.$buefy.toast.open({
                message: 'Abono eliminado correctamente',
                type: 'is-success'
              })
              this.obtenerAbonos()
            } else {
              this.$buefy.toast.open({
                message: respuesta.message || 'No se pudo eliminar el abono',
                type: 'is-danger'
              })
            }
          } catch (e) {
            this.$buefy.toast.open({
              message: 'Error al eliminar el abono',
              type: 'is-danger'
            })
          } finally {
            this.cargando = false
          }
        }
      })
    },

    formatoFechaCaracas (fecha)
    {
      if (!fecha) return 'Fecha inválida';
      const [anio, mes, dia] = fecha.split('-');
      return `${dia}-${mes}-${anio}`;
    },

    async obtenerAbonos ()
    {
      this.cargando = true
      let payload = {
        accion: 'obtener_abonos',
        id: this.$route.params.id,
        pagina: this.perPage === null ? null : this.currentPage,
        limite: this.perPage,
      }

      const datos = await HttpService.obtenerConConsultas('ventas.php', payload)
      const { abonos, cuentaApartado, total } = datos

      this.abonos = abonos
      this.cuentaApartado = cuentaApartado
      this.total = total
      this.datosCuentaApartado = [
        {
          nombre: 'Nombre del Cliente',
          total: cuentaApartado.nombreCliente,
          icono: 'account',
          clase: 'has-text-primary',
        },
        {
          nombre: 'N0 Factura',
          total: cuentaApartado.id,
          icono: 'account',
          clase: 'has-text-primary',
        },
        {
          nombre: 'Monto total',
          total: this.formatoMonto(cuentaApartado.total),
          icono: 'cash',
          clase: 'has-text-info',
        },
        {
          nombre: 'Monto pagado',
          total: this.formatoMonto(cuentaApartado.pagado),
          icono: 'check-circle',
          clase: 'has-text-success',
        },
        {
          nombre: 'Monto por pagar',
          total: `$${this.formatoMonto(cuentaApartado.porPagar)}`,
          icono: 'clock',
          clase: 'has-text-danger',
        },
        {
          nombre: 'Monto por pagar',
          total: `Bs.${this.formatoMonto(Math.round(cuentaApartado.porPagar * this.tasa * 100) / 100)}`,
          icono: 'clock',
          clase: 'has-text-danger',
        },
      ]

      this.cargando = false
    },

    onPageChange (page)
    {
      this.currentPage = page
      this.obtenerAbonos()
    },

    onPerPageChange ()
    {
      this.currentPage = 1
      this.obtenerAbonos()
    }
  }
}
</script>
